import {
  AutomationIntegrationDeploymentApiV1GetHealthcheckInfoApiResponse,
  useAutomationIntegrationDeploymentApiV1DispatchHealthcheckRequestsMutation,
  useAutomationIntegrationDeploymentApiV1GetHealthcheckInfoQuery,
} from "@providers/hop-ord-server/api";
import { useCallback, useEffect, useRef, useState } from "react";

const MAX_POLL_ATTEMPTS = 10;

export const useHealthcheck = (
  tenantIds: number[],
  skip?: boolean,
): {
  healthcheckData:
    | AutomationIntegrationDeploymentApiV1GetHealthcheckInfoApiResponse
    | undefined;
  isHealthcheckLoading: boolean;
  doHealthcheck: () => Promise<void>;
  stopPolling: () => void;
} => {
  const [dispatchHealthcheck] =
    useAutomationIntegrationDeploymentApiV1DispatchHealthcheckRequestsMutation();

  const [polling, setPolling] = useState(false);
  const attemptsRef = useRef(0);

  const {
    data: healthcheckData,
    refetch: pollHealthcheck,
    isLoading,
    isFetching,
  } = useAutomationIntegrationDeploymentApiV1GetHealthcheckInfoQuery(
    { tenantIds },
    {
      refetchOnMountOrArgChange: true,
      skip,
    },
  );

  const timerHandle = useRef<NodeJS.Timeout | undefined>(undefined);

  const stopPolling = useCallback(() => {
    setPolling(false);
    clearInterval(timerHandle.current);
    timerHandle.current = undefined;
    attemptsRef.current = 0;
  }, []);

  const doHealthcheck = useCallback(async () => {
    if (skip) {
      return;
    }
    setPolling(true);

    // dispatch
    try {
      await dispatchHealthcheck({ tenantIds }).unwrap();
    } catch (error) {
      stopPolling();
      console.error("Error with health check:", error);
      return;
    }

    // setup polling
    timerHandle.current = setInterval(async () => {
      if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
        stopPolling();
        return;
      }
      attemptsRef.current += 1;
      try {
        const { data: healthcheckData, isError } = await pollHealthcheck();
        if (isError) {
          stopPolling();
          return;
        }
        if (!healthcheckData) {
          return;
        }
        const allServersLatest = Object.values(healthcheckData).every(
          (server) => server.isLatest === true,
        );
        if (allServersLatest) {
          stopPolling();
        }
      } catch (error) {
        console.error("Error with health check:", error);
        stopPolling();
      }
    }, 2_000);
  }, [tenantIds, dispatchHealthcheck, pollHealthcheck, stopPolling, skip]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    healthcheckData,
    doHealthcheck,
    stopPolling,
    isHealthcheckLoading: isLoading || isFetching || polling,
  };
};
